import { motion } from 'framer-motion';

interface LoadingScreenProps {
  /** 0–100 overall load progress. */
  progress: number;
}

export function LoadingScreen({ progress }: LoadingScreenProps) {
  const pct = Math.max(0, Math.min(100, Math.round(progress)));

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="absolute inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden pointer-events-auto"
    >
      {/* Deep dark background */}
      <div className="absolute inset-0 bg-[#030712]" />

      {/* Subtle star field */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background: `
            radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.25) 0%, transparent 100%),
            radial-gradient(1px 1px at 60% 20%, rgba(255,255,255,0.25) 0%, transparent 100%),
            radial-gradient(1px 1px at 80% 50%, rgba(255,255,255,0.15) 0%, transparent 100%),
            radial-gradient(1.5px 1.5px at 30% 70%, rgba(0,184,169,0.25) 0%, transparent 100%),
            radial-gradient(1.5px 1.5px at 75% 80%, rgba(0,184,169,0.2) 0%, transparent 100%)
          `,
          animation: 'loaderStarDrift 60s linear infinite',
        }}
      />

      {/* Central content */}
      <div className="relative z-10 flex flex-col items-center text-center w-[320px]">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-slate-400 text-sm uppercase tracking-[0.3em] font-light mb-6"
        >
          Building the digital twin
        </motion.p>

        {/* Progress bar */}
        <div className="w-full h-[3px] rounded-full bg-white/10 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-[#00b8a9]"
            style={{ boxShadow: '0 0 12px rgba(0,184,169,0.7)' }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>

        {/* Percentage */}
        <div className="mt-4 text-[#00b8a9] text-2xl font-light tabular-nums tracking-wider">
          {pct}%
        </div>
      </div>

      <style>{`
        @keyframes loaderStarDrift {
          0% { transform: translateY(0px) translateX(0px); }
          100% { transform: translateY(-20px) translateX(10px); }
        }
      `}</style>
    </motion.div>
  );
}
